import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Trash2, Heart, MessageCircle, Sparkles, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function MatchingTool({ eventId, isOwner }) {
  const [questions, setQuestions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState(localStorage.getItem("matching_user_name") || "");
  const [answers, setAnswers] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  // Question creation (owner)
  const [newQuestions, setNewQuestions] = useState([
    { question_text: "", question_type: "multiple_choice", options: ["", ""] }
  ]);

  useEffect(() => {
    fetchQuestions();
    fetchParticipants();
  }, [eventId]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/matching/questions`);
      setQuestions(response.data);
      
      // Check if user already submitted
      const savedName = localStorage.getItem("matching_user_name");
      if (savedName) {
        const participants = await axios.get(`${API}/events/${eventId}/matching/participants`);
        const hasAnswered = participants.data.some(p => p.participant_name === savedName);
        setHasSubmitted(hasAnswered);
      }
    } catch (error) {
      console.error("Failed to fetch questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/matching/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error("Failed to fetch participants:", error);
    }
  };

  const handleSaveQuestions = async () => {
    // Validate
    const validQuestions = newQuestions.filter(q => q.question_text.trim() !== "");
    
    if (validQuestions.length === 0) {
      toast.error("Bitte mindestens eine Frage hinzufügen!");
      return;
    }

    // Validate multiple choice options
    for (const q of validQuestions) {
      if (q.question_type === "multiple_choice") {
        const validOptions = q.options.filter(opt => opt.trim() !== "");
        if (validOptions.length < 2) {
          toast.error("Multiple Choice Fragen brauchen mindestens 2 Optionen!");
          return;
        }
        q.options = validOptions;
      }
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/matching/questions`,
        { questions: validQuestions },
        { withCredentials: true }
      );
      toast.success("Fragen gespeichert!");
      fetchQuestions();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleSubmitAnswers = async () => {
    if (!userName.trim()) {
      toast.error("Bitte gib deinen Namen ein!");
      return;
    }

    // Check if all questions are answered
    const unanswered = questions.filter(q => !answers[q.id] || answers[q.id] === "");
    if (unanswered.length > 0) {
      toast.error("Bitte alle Fragen beantworten!");
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/matching/submit`, {
        participant_name: userName,
        answers: answers
      });
      
      localStorage.setItem("matching_user_name", userName);
      setHasSubmitted(true);
      toast.success("Antworten gespeichert! Jetzt Matches anschauen 🎯");
      fetchParticipants();
      fetchMatches();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const fetchMatches = async () => {
    if (!userName.trim()) return;

    try {
      const response = await axios.get(`${API}/events/${eventId}/matching/matches/${userName}`);
      setMatches(response.data);
    } catch (error) {
      console.error("Failed to fetch matches:", error);
    }
  };

  const addQuestion = () => {
    setNewQuestions([...newQuestions, { question_text: "", question_type: "multiple_choice", options: ["", ""] }]);
  };

  const removeQuestion = (index) => {
    setNewQuestions(newQuestions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...newQuestions];
    updated[index][field] = value;
    setNewQuestions(updated);
  };

  const addOption = (qIndex) => {
    const updated = [...newQuestions];
    updated[qIndex].options.push("");
    setNewQuestions(updated);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const updated = [...newQuestions];
    updated[qIndex].options[optIndex] = value;
    setNewQuestions(updated);
  };

  const removeOption = (qIndex, optIndex) => {
    const updated = [...newQuestions];
    updated[qIndex].options = updated[qIndex].options.filter((_, i) => i !== optIndex);
    setNewQuestions(updated);
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 80) return "from-green-500/20 to-emerald-500/20 border-green-500";
    if (percentage >= 60) return "from-blue-500/20 to-cyan-500/20 border-blue-500";
    if (percentage >= 40) return "from-yellow-500/20 to-orange-500/20 border-yellow-500";
    return "from-gray-400/20 to-gray-500/20 border-gray-400";
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Users className="w-5 h-5 text-primary" />
          Matching-Tool
        </h3>
        <p className="text-sm text-muted-foreground">
          Finde Gäste mit ähnlichen Interessen! 🤝
        </p>
      </div>

      {isOwner ? (
        // Owner View: Create Questions
        <div className="space-y-6">
          <div className="p-4 bg-secondary/20 rounded-lg">
            <h4 className="font-medium mb-2">📝 Matching-Fragen erstellen</h4>
            <p className="text-sm text-muted-foreground">
              Erstelle Fragen, um Gäste basierend auf Gemeinsamkeiten zu matchen.
            </p>
          </div>

          {questions.length > 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="font-medium text-green-800">Aktive Fragen: {questions.length}</p>
              </div>
              <p className="text-sm text-green-700">{participants.length} Teilnehmer haben bereits geantwortet</p>
            </div>
          )}

          <div className="space-y-4">
            {newQuestions.map((q, qIndex) => (
              <div key={qIndex} className="p-4 border rounded-lg bg-card space-y-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label>Frage {qIndex + 1}</Label>
                      <Input
                        value={q.question_text}
                        onChange={(e) => updateQuestion(qIndex, "question_text", e.target.value)}
                        placeholder="z.B. Was ist dein liebstes Hobby?"
                      />
                    </div>

                    <div>
                      <Label>Antwort-Typ</Label>
                      <Select
                        value={q.question_type}
                        onValueChange={(val) => updateQuestion(qIndex, "question_type", val)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                          <SelectItem value="yes_no">Ja/Nein</SelectItem>
                          <SelectItem value="text">Freitext</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {q.question_type === "multiple_choice" && (
                      <div className="space-y-2">
                        <Label>Optionen</Label>
                        {q.options.map((opt, optIndex) => (
                          <div key={optIndex} className="flex gap-2">
                            <Input
                              value={opt}
                              onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                              className="flex-1"
                            />
                            {q.options.length > 2 && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => removeOption(qIndex, optIndex)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => addOption(qIndex)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Option hinzufügen
                        </Button>
                      </div>
                    )}
                  </div>

                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeQuestion(qIndex)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Button onClick={addQuestion} variant="outline" className="flex-1">
              <Plus className="w-4 h-4 mr-2" />
              Frage hinzufügen
            </Button>
            <Button onClick={handleSaveQuestions} className="flex-1">
              Fragen speichern
            </Button>
          </div>
        </div>
      ) : (
        // Guest View: Answer Questions & See Matches
        <Tabs defaultValue={hasSubmitted ? "matches" : "quiz"} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="quiz">
              <MessageCircle className="w-4 h-4 mr-2" />
              Fragen
            </TabsTrigger>
            <TabsTrigger value="matches" disabled={!hasSubmitted}>
              <Heart className="w-4 h-4 mr-2" />
              Matches
            </TabsTrigger>
          </TabsList>

          {/* Quiz Tab */}
          <TabsContent value="quiz" className="space-y-4">
            {questions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Noch keine Matching-Fragen</p>
                <p className="text-xs mt-2">Der Gastgeber hat noch keine Fragen erstellt</p>
              </div>
            ) : (
              <>
                {/* User Name Input */}
                <div className="p-4 bg-secondary/20 rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Dein Name</Label>
                  <Input
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Name eingeben..."
                    disabled={hasSubmitted}
                  />
                </div>

                {/* Questions */}
                <div className="space-y-4">
                  {questions.map((q, index) => (
                    <div key={q.id} className="p-4 border rounded-lg bg-card">
                      <Label className="text-base font-medium mb-3 block">
                        {index + 1}. {q.question_text}
                      </Label>

                      {q.question_type === "multiple_choice" && (
                        <Select
                          value={answers[q.id] || ""}
                          onValueChange={(val) => setAnswers({ ...answers, [q.id]: val })}
                          disabled={hasSubmitted}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Option wählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {q.options.map((opt, idx) => (
                              <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {q.question_type === "yes_no" && (
                        <div className="flex gap-3">
                          <Button
                            variant={answers[q.id] === "Ja" ? "default" : "outline"}
                            onClick={() => setAnswers({ ...answers, [q.id]: "Ja" })}
                            disabled={hasSubmitted}
                            className="flex-1"
                          >
                            👍 Ja
                          </Button>
                          <Button
                            variant={answers[q.id] === "Nein" ? "default" : "outline"}
                            onClick={() => setAnswers({ ...answers, [q.id]: "Nein" })}
                            disabled={hasSubmitted}
                            className="flex-1"
                          >
                            👎 Nein
                          </Button>
                        </div>
                      )}

                      {q.question_type === "text" && (
                        <Textarea
                          value={answers[q.id] || ""}
                          onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                          placeholder="Deine Antwort..."
                          rows={2}
                          disabled={hasSubmitted}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {!hasSubmitted && (
                  <Button onClick={handleSubmitAnswers} className="w-full" size="lg">
                    Antworten absenden & Matches finden
                  </Button>
                )}

                {hasSubmitted && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-medium text-green-800">Antworten gespeichert!</p>
                    <p className="text-sm text-green-700 mt-1">Schau dir jetzt deine Matches an →</p>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches" className="space-y-4">
            <Button onClick={fetchMatches} variant="outline" className="w-full">
              Matches aktualisieren
            </Button>

            <div className="space-y-4">
              {matches.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Heart className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Noch keine Matches</p>
                  <p className="text-xs mt-2">Warte darauf, dass andere Gäste antworten</p>
                </div>
              ) : (
                matches.map((match, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 bg-gradient-to-r ${getMatchColor(match.match_percentage)}`}
                  >
                    {/* Match Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                          <span className="text-2xl">👤</span>
                        </div>
                        <div>
                          <p className="font-semibold text-lg">{match.participant_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {match.total_common} gemeinsame Antworten
                          </p>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary">{match.match_percentage}%</p>
                        <p className="text-xs text-muted-foreground">Match</p>
                      </div>
                    </div>

                    {/* Common Answers */}
                    {match.common_answers && match.common_answers.length > 0 && (
                      <div className="mb-3 p-3 bg-white/50 rounded">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Gemeinsame Interessen:
                        </p>
                        <div className="space-y-1">
                          {match.common_answers.slice(0, 3).map((common, idx) => (
                            <p key={idx} className="text-sm">
                              • <span className="font-medium">{common.question}:</span> {common.answer}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Icebreakers */}
                    {match.icebreakers && match.icebreakers.length > 0 && (
                      <div className="p-3 bg-primary/10 rounded">
                        <p className="text-xs font-medium text-primary mb-2 flex items-center gap-1">
                          💬 Gesprächsstarter:
                        </p>
                        <div className="space-y-1">
                          {match.icebreakers.map((icebreaker, idx) => (
                            <p key={idx} className="text-sm italic">&quot;{icebreaker}&quot;</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </Card>
  );
}
